import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle } from 'lucide-react';
import { ConfirmSubmitDialog } from './ConfirmSubmitDialog';
import { SeniorLockNotice, SeniorLockBlockedMessage } from './SeniorLockNotice';
import { GateSequenceNotice, GateSequenceBlockedMessage } from './GateSequenceNotice';
import { resolveSeniorLock } from './seniorLock';
import { resolveGateSequence, type GatePrerequisiteState } from './gatePrerequisites';
import { DecisionImpactNote } from './reviewUtils';
import { serverDetail } from './serverDetail';
import { gateDecisionLabel, finalDecisionLabel } from '@/lib/enumLabels';
import type { FinalDecision, GateDecision } from '@/types';
import type { AnnotationMap } from './AnnotationForm';
import { cn } from '@/lib/utils';

export type DecisionType = 'ACCEPT' | 'OVERRIDE' | 'ESCALATE';

interface ReviewDecisionFormProps {
  aiDecision: GateDecision;
  reviewerName: string;
  blockingCount: number;
  annotatedBlockingCount: number;
  annotations: AnnotationMap;
  onSubmit: (payload: ReviewPayload) => Promise<void>;
  /** Gate raised INV-TYPE-01, INV-LAW-01, or INV-POL-01 on this job. */
  requiresSeniorApproval?: boolean;
  /** Why the gate locked it, shown verbatim so the reviewer knows what to escalate. */
  seniorApprovalReasons?: string[];
  /** Caller holds senior_reviewer or admin. */
  canApproveSeniorLocked?: boolean;
  /**
   * Approval state of Gate 1 / Gate 2, from
   * GET /jobs/{id}/review/prerequisites. Null while loading or if the call
   * failed — the server still enforces the sequence either way.
   */
  gatePrerequisites?: GatePrerequisiteState | null;
  className?: string;
}

export interface ReviewPayload {
  decision_type: 'accept' | 'override' | 'escalate';
  final_decision: FinalDecision | null;
  justification: string;
  annotations_summary: { agree: number; disagree: number; discuss: number };
}

const OVERRIDE_OPTIONS: { value: FinalDecision; label: string }[] = [
  { value: 'APPROVED', label: finalDecisionLabel.APPROVED },
  { value: 'APPROVED_WITH_ALERTS', label: finalDecisionLabel.APPROVED_WITH_ALERTS },
  { value: 'REJECTED', label: finalDecisionLabel.REJECTED },
  { value: 'ESCALATED', label: finalDecisionLabel.ESCALATED },
];

const DECISION_TYPE_META: Record<DecisionType, { title: string; sub: (ai: GateDecision) => string }> = {
  ACCEPT: { title: 'Accept AI Recommendation', sub: (ai) => `Confirm the AI recommendation: ${gateDecisionLabel[ai]}` },
  OVERRIDE: { title: 'Override: Change Decision', sub: () => 'Your judgment differs from the AI' },
  ESCALATE: { title: 'Escalate', sub: () => 'Cannot decide alone — escalate to senior review' },
};

/**
 * Final review decision form: Accept / Override / Escalate.
 * FR-077: labels clearly state the contract is activated/rejected/escalated.
 * WCAG: fieldset/legend for decision type radios; aria-required, aria-invalid,
 *       aria-describedby on justification; confirmation dialog before submit.
 */
export function ReviewDecisionForm({
  aiDecision,
  reviewerName,
  blockingCount,
  annotatedBlockingCount,
  annotations,
  onSubmit,
  requiresSeniorApproval = false,
  seniorApprovalReasons = [],
  canApproveSeniorLocked = false,
  gatePrerequisites = null,
  className,
}: ReviewDecisionFormProps) {
  const [decisionType, setDecisionType] = useState<DecisionType | null>(null);
  const [overrideTo, setOverrideTo] = useState<FinalDecision | null>(null);
  const [justification, setJustification] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justError, setJustError] = useState<string | null>(null);

  const isOverride = decisionType === 'OVERRIDE';
  const isEscalate = decisionType === 'ESCALATE';
  // Justification is mandatory (>=20 chars) only for OVERRIDE. Escalate ("send
  // it back") accepts notes with or without text — an empty note auto-fills a
  // default so the backend contract is unchanged.
  const needsJustification = isOverride;
  const justMin = needsJustification ? 20 : 0;

  // Map GateDecision → FinalDecision: MANUAL_REVIEW_REQUIRED has no equivalent, treat as ESCALATED.
  const gatToFinal = (gd: GateDecision): FinalDecision => gd === 'MANUAL_REVIEW_REQUIRED' ? 'ESCALATED' : gd;
  const effectiveDecision: FinalDecision | null =
    decisionType === 'ACCEPT' ? gatToFinal(aiDecision) :
    decisionType === 'OVERRIDE' ? overrideTo :
    decisionType === 'ESCALATE' ? 'ESCALATED' : null;

  function buildSummary() {
    let agree = 0; let disagree = 0; let discuss = 0;
    for (const { action } of annotations.values()) {
      if (action === 'AGREE') agree++;
      else if (action === 'DISAGREE') disagree++;
      else discuss++;
    }
    return { agree, disagree, discuss };
  }

  function validate(): boolean {
    if (needsJustification && justification.trim().length < justMin) {
      setJustError(`Justification required (minimum ${justMin} characters).`);
      return false;
    }
    if (isOverride && !overrideTo) {
      setJustError('Select a new decision for the override.');
      return false;
    }
    setJustError(null);
    return true;
  }

  async function handleConfirmedSubmit() {
    setShowDialog(false);
    setIsSubmitting(true);
    setSubmitError(null);
    const payload: ReviewPayload = {
      decision_type: decisionType === 'ACCEPT' ? 'accept' : decisionType === 'OVERRIDE' ? 'override' : 'escalate',
      final_decision: effectiveDecision,
      justification:
        justification.trim() ||
        (decisionType === 'ESCALATE'
          ? 'Escalated for senior review.'
          : 'Reviewed and agreed with AI recommendation.'),
      annotations_summary: buildSummary(),
    };
    try {
      await onSubmit(payload);
    } catch (err) {
      setSubmitError(
        serverDetail(err) ??
          'Failed to record your decision. Please try again. Your work has been preserved.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const { locked: seniorLocked, blocked: blockedByLock } = resolveSeniorLock(
    requiresSeniorApproval, canApproveSeniorLocked, effectiveDecision,
  );
  const { outstanding, blocked: blockedByGates } = resolveGateSequence(
    gatePrerequisites, effectiveDecision,
  );
  const submitDisabled =
    !decisionType || !confirmed || isSubmitting || blockedByLock || blockedByGates;

  return (
    <div className={cn('space-y-5', className)}>
      {seniorLocked && <SeniorLockNotice reasons={seniorApprovalReasons} />}
      {outstanding.length > 0 && gatePrerequisites && (
        <GateSequenceNotice prerequisites={gatePrerequisites.prerequisites} />
      )}

      {/* Unannotated blocking issues warning */}
      {blockingCount > 0 && annotatedBlockingCount < blockingCount && (
        <Alert>
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            {blockingCount - annotatedBlockingCount} of {blockingCount} blocking issue{blockingCount !== 1 ? 's' : ''} not yet annotated.
            Review all blocking issues before making a decision.
          </AlertDescription>
        </Alert>
      )}

      {/* Decision type — WCAG fieldset/legend */}
      <fieldset>
        <legend className="text-sm font-semibold mb-3">Select your decision</legend>
        <div className="space-y-2" role="radiogroup">
          {(['ACCEPT', 'OVERRIDE', 'ESCALATE'] as DecisionType[]).map((type) => {
            const meta = DECISION_TYPE_META[type];
            const isActive = decisionType === type;
            return (
              <button key={type} type="button" role="radio" aria-checked={isActive}
                aria-label={meta.title}
                onClick={() => { setDecisionType(type); setJustError(null); }}
                className={cn(
                  'w-full text-left rounded-lg border px-4 py-3 transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/30 bg-background',
                )}
              >
                <p className="text-sm font-medium">{meta.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{meta.sub(aiDecision)}</p>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Override target */}
      {isOverride && (
        <div>
          <label htmlFor="override-select" className="text-sm font-medium block mb-1">
            New decision <span aria-hidden="true" className="text-destructive">*</span>
          </label>
          <select id="override-select" value={overrideTo ?? ''} aria-required="true"
            onChange={(e) => { setOverrideTo(e.target.value as FinalDecision); }}
            className="w-full rounded-md border border-input bg-background text-sm px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="" disabled>Select...</option>
            {OVERRIDE_OPTIONS.filter((o) => o.value !== gatToFinal(aiDecision)).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <DecisionImpactNote decision={overrideTo} />
        </div>
      )}
      {decisionType === 'ACCEPT' && <DecisionImpactNote decision={gatToFinal(aiDecision)} />}
      {isEscalate && <DecisionImpactNote decision="ESCALATED" />}

      {/* Justification — WCAG aria-required, aria-invalid, aria-describedby */}
      <div>
        <label htmlFor="review-justification" className="text-sm font-medium block mb-1">
          Justification {needsJustification && <span aria-hidden="true" className="text-destructive">*</span>}
        </label>
        <textarea id="review-justification" value={justification} rows={4}
          onChange={(e) => { setJustification(e.target.value); if (justError) setJustError(null); }}
          placeholder={
            needsJustification
              ? `Explain your decision (min ${justMin} characters)...`
              : isEscalate
                ? 'Optional: note for whoever this is sent back to…'
                : 'Reviewed and agreed with AI recommendation.'
          }
          aria-required={needsJustification} aria-invalid={justError !== null}
          aria-describedby={justError ? 'just-error' : undefined}
          className={cn(
            'w-full text-sm rounded-md border bg-background px-3 py-2 resize-none',
            'placeholder:text-muted-foreground focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            justError ? 'border-destructive' : 'border-input',
          )}
        />
        <div className="flex justify-between mt-1">
          {justError ? <p id="just-error" role="alert" className="text-xs text-destructive">{justError}</p> : <span />}
          {needsJustification && <p className="text-xs text-muted-foreground">{justification.length}/{justMin} min</p>}
        </div>
      </div>

      {/* Sign-off */}
      <div className="border rounded-lg p-4 bg-muted/30 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sign-off</p>
        <p className="text-sm"><span className="text-muted-foreground">Reviewer: </span>{reviewerName}</p>
        <label className="flex items-start gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={confirmed} aria-required="true"
            onChange={(e) => { setConfirmed(e.target.checked); }}
            className="mt-0.5 h-4 w-4 rounded border-input focus-visible:ring-2 focus-visible:ring-ring"
          />
          <span>I confirm I have reviewed the gate report and the original contract</span>
        </label>
      </div>

      {submitError && <p role="alert" className="text-xs text-destructive">{submitError}</p>}

      {blockedByLock && <SeniorLockBlockedMessage />}
      {blockedByGates && <GateSequenceBlockedMessage outstanding={outstanding} />}

      <Button onClick={() => { if (validate()) setShowDialog(true); }}
        disabled={submitDisabled} aria-disabled={submitDisabled} className="w-full" size="lg">
        {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
        {isSubmitting ? 'Recording decision...' : 'Submit Review'}
      </Button>

      {showDialog && (
        <ConfirmSubmitDialog
          decision={effectiveDecision}
          decisionType={decisionType}
          onConfirm={() => { void handleConfirmedSubmit(); }}
          onCancel={() => { setShowDialog(false); }}
        />
      )}
    </div>
  );
}
